import React, { useState, useEffect, useMemo } from 'react';
import { 
  Smartphone, Gift, AlertCircle, CheckCircle, Edit2, Search, RefreshCw,
  MapPin, HelpCircle, ChevronRight, DollarSign, Zap, Calendar, Info,
  ExternalLink, Phone, MessageSquare, Loader2, X, Signal
} from 'lucide-react';
import { useProfile } from '../ProfileContext';
import SectionWrapper from '../components/SectionWrapper';
import { supabase } from '../../../lib/supabase';

// Cellular carriers with their streaming perks
const CELLULAR_CARRIERS = [
  { 
    name: 'T-Mobile', 
    code: 'tmobile',
    perks: [
      { service: 'Netflix', tier: 'Standard (2 screens)', value: 15.49, plans: ['Magenta MAX', 'Go5G Plus', 'Go5G Next'] },
      { service: 'Apple TV+', tier: 'Full', value: 9.99, plans: ['Magenta MAX', 'Go5G Plus', 'Go5G Next', 'Go5G'] },
      { service: 'Hulu', tier: 'With Ads', value: 7.99, plans: ['Go5G Next'] },
    ],
    website: 'https://www.t-mobile.com/account',
    phone: '1-800-937-8997',
  },
  { 
    name: 'Verizon', 
    code: 'verizon',
    perks: [
      { service: 'Disney+ Bundle', tier: 'Disney+, Hulu, ESPN+', value: 14.99, plans: ['Unlimited Ultimate', 'Unlimited Plus', 'myPlan with Disney Bundle'] },
      { service: 'Apple One', tier: 'Individual', value: 19.95, plans: ['Unlimited Ultimate'] },
      { service: 'Apple Music', tier: 'Family', value: 16.99, plans: ['Unlimited Plus', 'myPlan'] },
      { service: '+play Credits', tier: '$10-15/mo', value: 10.00, plans: ['Unlimited Plus', 'Unlimited Ultimate'] },
    ],
    website: 'https://www.verizon.com/signin',
    phone: '1-800-922-0204',
  },
  { 
    name: 'AT&T', 
    code: 'att',
    perks: [
      { service: 'Max (HBO)', tier: 'With Ads', value: 9.99, plans: ['Unlimited Premium PL', 'Unlimited Elite'] },
      { service: 'Apple TV+', tier: 'Full', value: 9.99, plans: ['Unlimited Premium PL'] },
    ],
    website: 'https://www.att.com/my',
    phone: '1-800-331-0500',
  },
  { 
    name: 'Visible (Verizon)', 
    code: 'visible',
    perks: [],
    website: 'https://www.visible.com/account',
    phone: null,
  },
  { 
    name: 'Mint Mobile', 
    code: 'mint',
    perks: [],
    website: 'https://my.mintmobile.com',
    phone: '1-800-683-7392',
  },
  { 
    name: 'Google Fi', 
    code: 'googlefi',
    perks: [
      { service: 'YouTube Premium', tier: 'Individual', value: 13.99, plans: ['Unlimited Plus'] },
    ],
    website: 'https://fi.google.com/account',
    phone: '1-844-825-5234',
  },
  { 
    name: 'US Cellular', 
    code: 'uscellular',
    perks: [],
    website: 'https://www.uscellular.com/my-account',
    phone: '1-888-944-9400',
  },
  { 
    name: 'Cricket Wireless', 
    code: 'cricket',
    perks: [],
    website: 'https://www.cricketwireless.com/myaccount',
    phone: '1-800-274-2538',
  },
  { 
    name: 'Metro by T-Mobile', 
    code: 'metro',
    perks: [
      { service: 'Amazon Prime', tier: 'Full', value: 14.99, plans: ['Unlimited Premium'] },
    ],
    website: 'https://www.metrobyt-mobile.com/my-account',
    phone: '1-888-863-8768',
  },
  { 
    name: 'Boost Mobile', 
    code: 'boost',
    perks: [],
    website: 'https://my.boostmobile.com',
    phone: '1-833-502-6678',
  },
];

// Cellular plans data (simplified - would come from database in production)
const CELLULAR_PLANS = {
  tmobile: [
    { id: 't1', name: 'Go5G', price: 75, lines: 1, data: 'Unlimited', perks: ['Apple TV+'] },
    { id: 't2', name: 'Go5G Plus', price: 90, lines: 1, data: 'Unlimited Premium', perks: ['Netflix Standard', 'Apple TV+'] },
    { id: 't3', name: 'Go5G Next', price: 100, lines: 1, data: 'Unlimited Premium+', perks: ['Netflix Standard', 'Apple TV+', 'Hulu'] },
    { id: 't4', name: 'Essentials', price: 60, lines: 1, data: 'Unlimited', perks: [] },
  ],
  verizon: [
    { id: 'v1', name: 'Unlimited Welcome', price: 65, lines: 1, data: 'Unlimited', perks: [] },
    { id: 'v2', name: 'Unlimited Plus', price: 80, lines: 1, data: 'Unlimited Premium', perks: ['Disney Bundle', 'Apple Music Family', '+play $10'] },
    { id: 'v3', name: 'Unlimited Ultimate', price: 90, lines: 1, data: 'Unlimited Premium+', perks: ['Disney Bundle', 'Apple One', '+play $15'] },
    { id: 'v4', name: 'myPlan', price: 70, lines: 1, data: 'Unlimited', perks: ['Pick your perks'] },
  ],
  att: [
    { id: 'a1', name: 'Unlimited Starter', price: 65, lines: 1, data: 'Unlimited', perks: [] },
    { id: 'a2', name: 'Unlimited Extra', price: 75, lines: 1, data: 'Unlimited+', perks: [] },
    { id: 'a3', name: 'Unlimited Premium PL', price: 85, lines: 1, data: 'Unlimited Premium', perks: ['Max (HBO)', 'Apple TV+'] },
  ],
  googlefi: [
    { id: 'g1', name: 'Flexible', price: 20, lines: 1, data: 'Pay per GB', perks: [] },
    { id: 'g2', name: 'Simply Unlimited', price: 50, lines: 1, data: 'Unlimited', perks: [] },
    { id: 'g3', name: 'Unlimited Plus', price: 65, lines: 1, data: 'Unlimited Premium', perks: ['YouTube Premium'] },
  ],
  metro: [
    { id: 'm1', name: '5G Start', price: 40, lines: 1, data: '10GB', perks: [] },
    { id: 'm2', name: '5G Unlimited', price: 50, lines: 1, data: 'Unlimited', perks: [] },
    { id: 'm3', name: 'Unlimited Premium', price: 60, lines: 1, data: 'Unlimited Premium', perks: ['Amazon Prime'] },
  ],
};

const CellularSection = () => {
  const {
    profile,
    refData,
    expandedSections,
    saving,
    activePerks,
    saveProfile,
    toggleSection,
    isSectionDirty,
  } = useProfile();

  const isExpanded = expandedSections.includes('cellular');
  const isDirty = isSectionDirty('cellular');

  // Local state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBetterPlansModal, setShowBetterPlansModal] = useState(false);
  const [showCompareProvidersModal, setShowCompareProvidersModal] = useState(false);

  // Get current carrier info
  const currentCarrier = useMemo(() => {
    if (!profile.cellularProvider) return null;
    return CELLULAR_CARRIERS.find(c => 
      c.name.toLowerCase() === profile.cellularProvider?.toLowerCase() ||
      c.code === profile.cellularProvider?.toLowerCase()
    );
  }, [profile.cellularProvider]);

  // Get perks for current carrier/plan
  const currentCarrierPerks = useMemo(() => {
    if (!currentCarrier) return [];
    
    // If we have a specific plan, filter perks that apply to it
    if (profile.cellularPlan) {
      return currentCarrier.perks.filter(perk => 
        perk.plans.some(p => 
          p.toLowerCase().includes(profile.cellularPlan?.toLowerCase()) ||
          profile.cellularPlan?.toLowerCase().includes(p.toLowerCase())
        )
      );
    }
    
    // Otherwise return all perks for this carrier
    return currentCarrier.perks;
  }, [currentCarrier, profile.cellularPlan]);

  // Get active cellular perks from activePerks
  const activeCellularPerks = useMemo(() => {
    if (!currentCarrier) return [];
    
    const carrierLower = currentCarrier.name.toLowerCase();
    return activePerks.filter(perk => {
      const source = perk.perk_source?.toLowerCase() || '';
      return source.includes(carrierLower) || 
             source.includes(currentCarrier.code) ||
             source.includes('cellular') ||
             source.includes('mobile');
    });
  }, [activePerks, currentCarrier]);

  // Calculate unclaimed perks
  const unclaimedPerks = useMemo(() => {
    return currentCarrierPerks.filter(refPerk => 
      !activeCellularPerks.some(activePerk => 
        activePerk.service_name?.toLowerCase().includes(refPerk.service.toLowerCase()) ||
        refPerk.service.toLowerCase().includes(activePerk.service_name?.toLowerCase())
      )
    );
  }, [currentCarrierPerks, activeCellularPerks]);

  // Calculate streaming value
  const totalStreamingValue = useMemo(() => {
    return currentCarrierPerks.reduce((total, perk) => total + (perk.value || 0), 0);
  }, [currentCarrierPerks]);

  // Get current plan info
  const currentPlanInfo = useMemo(() => {
    if (!profile.cellularPlan || !currentCarrier) return null;
    const plans = CELLULAR_PLANS[currentCarrier.code] || [];
    return plans.find(p => 
      p.name.toLowerCase() === profile.cellularPlan?.toLowerCase() ||
      profile.cellularPlan?.toLowerCase().includes(p.name.toLowerCase())
    );
  }, [profile.cellularPlan, currentCarrier]);

  // Build summary
  let summary = profile.cellularProvider || 'Not set';
  if (profile.cellularProvider) {
    const parts = [profile.cellularProvider];
    if (profile.cellularPlan) parts.push(profile.cellularPlan);
    if (currentCarrierPerks.length > 0) {
      parts.push(`${currentCarrierPerks.length} perk${currentCarrierPerks.length > 1 ? 's' : ''}`);
    }
    summary = parts.join(' • ');
  }

const handleSave = async (data) => {
  await saveProfile({
    zipCode: profile.zipCode,
    internetProvider: profile.internetProvider,
    internetPlan: profile.internetPlan,
    internetMonthlyCost: profile.internetMonthlyCost || null,
    cellularProvider: data.cellularProvider || null,
    cellularPlan: data.cellularPlan || null,
    cellularMonthlyCost: data.monthlyCost ? parseFloat(data.monthlyCost) : null,
    cellularLines: data.lines ? parseInt(data.lines) : 1,
    eligibility: profile.eligibility,
  });
  setShowEditModal(false);
};

  return (
    <SectionWrapper
      title="Cellular Provider"
      icon={Smartphone}
      isExpanded={isExpanded}
      onToggle={() => toggleSection('cellular')}
      isDirty={isDirty}
      isSaving={saving}
      onSave={saveProfile}
      summary={summary}
    >
      <div className="space-y-6">
        {/* Current Carrier Display */}
        {profile.cellularProvider ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">{profile.cellularProvider}</h4>
                </div>
                {profile.cellularPlan && (
                  <p className="text-sm text-gray-600 mt-1">
                    Plan: {profile.cellularPlan}
                    {profile.cellularLines > 1 && (
                      <span className="text-gray-400 ml-2">
                        ({profile.cellularLines} lines)
                      </span>
                    )}
                  </p>
                )}
                {profile.cellularMonthlyCost > 0 && (
                  <p className="text-sm text-gray-600">
                    ${parseFloat(profile.cellularMonthlyCost).toFixed(2)}/mo
                  </p>
                )}
                {totalStreamingValue > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    <Gift className="w-4 h-4 inline mr-1" />
                    ${totalStreamingValue.toFixed(2)}/mo in streaming perks
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowEditModal(true)}
                className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-blue-800 font-medium">
                  Set your cellular provider to discover streaming perks!
                </p>
                <p className="text-xs text-blue-700 mt-1 mb-3">
                  T-Mobile, Verizon, and AT&T include free streaming services on many plans.
                </p>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Add Cellular Provider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Streaming Perks */}
        {profile.cellularProvider && currentCarrierPerks.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-5 h-5 text-green-600" />
              <h4 className="font-medium text-green-900">
                Streaming Perks from {profile.cellularProvider}
              </h4>
            </div>
            
            <ul className="space-y-2">
              {currentCarrierPerks.map((perk, index) => {
                const isActive = activeCellularPerks.some(ap => 
                  ap.service_name?.toLowerCase().includes(perk.service.toLowerCase())
                );
                
                return (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    {isActive ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <span className="w-4 h-4 rounded-full border-2 border-gray-300" />
                    )}
                    <span className={isActive ? 'text-green-800 font-medium' : 'text-gray-600'}>
                      {perk.service}
                      {perk.tier && <span className="text-gray-500"> ({perk.tier})</span>}
                    </span>
                    {perk.value > 0 && (
                      <span className="text-xs text-gray-400 ml-auto">
                        ${perk.value.toFixed(2)}/mo value
                      </span>
                    )}
                    {isActive && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        Active
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            {unclaimedPerks.length > 0 && (
              <p className="mt-3 text-xs text-green-700 bg-green-100 p-2 rounded">
                💡 {unclaimedPerks.length} perk{unclaimedPerks.length > 1 ? 's' : ''} available! 
                Check your {profile.cellularProvider} app or account to activate.
              </p>
            )}

            {unclaimedPerks.length === 0 && activeCellularPerks.length > 0 && (
              <p className="mt-3 text-xs text-green-700 bg-green-100 p-2 rounded">
                ✓ You've activated all available perks from {profile.cellularProvider}!
              </p>
            )}
          </div>
        )}

        {/* No perks available notice */}
        {profile.cellularProvider && currentCarrierPerks.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-700">
                  {profile.cellularProvider} doesn't have streaming perks on record for your plan.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Check your provider's website for any current promotions.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Optimization Options */}
        {profile.cellularProvider && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">💡 Optimization Options</h4>
            
            <button
              onClick={() => setShowBetterPlansModal(true)}
              className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Check for better {profile.cellularProvider} plans</p>
                  <p className="text-sm text-gray-500">See if you can get more perks or lower price</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            <button
              onClick={() => setShowCompareProvidersModal(true)}
              className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Search className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Compare all carriers</p>
                  <p className="text-sm text-gray-500">Find the best cellular + streaming deals</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditCellularModal
          profile={profile}
          carriers={CELLULAR_CARRIERS}
          plans={CELLULAR_PLANS}
          onClose={() => setShowEditModal(false)}
          onSave={handleSave}
        />
      )}

      {/* Better Plans Modal */}
      {showBetterPlansModal && (
        <BetterCellularPlansModal
          profile={profile}
          currentCarrier={currentCarrier}
          currentPlan={currentPlanInfo}
          plans={CELLULAR_PLANS[currentCarrier?.code] || []}
          currentPerks={currentCarrierPerks}
          onClose={() => setShowBetterPlansModal(false)}
        />
      )}

      {/* Compare Providers Modal */}
      {showCompareProvidersModal && (
        <CompareCellularProvidersModal
          profile={profile}
          carriers={CELLULAR_CARRIERS}
          plans={CELLULAR_PLANS}
          currentCarrier={currentCarrier}
          onClose={() => setShowCompareProvidersModal(false)}
        />
      )}
    </SectionWrapper>
  );
};

// Edit Cellular Modal Component
const EditCellularModal = ({ profile, carriers, plans, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    cellularProvider: profile.cellularProvider || '',
    cellularPlan: profile.cellularPlan || '',
    monthlyCost: profile.cellularMonthlyCost || '',
    lines: profile.cellularLines || 1,
  });

  const selectedCarrier = carriers.find(c => c.name === formData.cellularProvider);
  const carrierPlans = selectedCarrier ? (plans[selectedCarrier.code] || []) : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Edit Cellular Provider</h3>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cellular Provider
            </label>
            <select
              value={formData.cellularProvider}
              onChange={(e) => setFormData({ ...formData, cellularProvider: e.target.value, cellularPlan: '' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select your carrier...</option>
              {carriers.map(carrier => (
                <option key={carrier.code} value={carrier.name}>
                  {carrier.name}
                  {carrier.perks.length > 0 && ` (${carrier.perks.length} streaming perks)`}
                </option>
              ))}
              <option value="other">Other / Not listed</option>
              <option value="none">No cellular service</option>
            </select>
          </div>

          {formData.cellularProvider && formData.cellularProvider !== 'other' && formData.cellularProvider !== 'none' && carrierPlans.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Plan
              </label>
              <select
                value={formData.cellularPlan}
                onChange={(e) => setFormData({ ...formData, cellularPlan: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select your plan...</option>
                {carrierPlans.map(plan => (
                  <option key={plan.id} value={plan.name}>
                    {plan.name} - ${plan.price}/line - {plan.data}
                    {plan.perks.length > 0 && ` (incl. ${plan.perks.join(', ')})`}
                  </option>
                ))}
                <option value="unknown">I'm not sure</option>
                <option value="other">Other / Legacy plan</option>
              </select>
              
              {/* Show perks for selected plan */}
              {formData.cellularPlan && (() => {
                const plan = carrierPlans.find(p => p.name === formData.cellularPlan);
                if (plan?.perks?.length > 0) {
                  return (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-medium text-green-800">Included streaming perks:</p>
                      <ul className="mt-1 text-sm text-green-700">
                        {plan.perks.map((perk, idx) => (
                          <li key={idx}>✓ {perk}</li>
                        ))}
                      </ul>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}

          {formData.cellularProvider && formData.cellularProvider !== 'none' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Lines
                </label>
                <select
                  value={formData.lines}
                  onChange={(e) => setFormData({ ...formData, lines: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                    <option key={n} value={n}>{n} line{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Cost (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthlyCost}
                    onChange={(e) => setFormData({ ...formData, monthlyCost: e.target.value })}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Total bill including all lines, taxes, and fees</p>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(formData)}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Better Cellular Plans Modal
const BetterCellularPlansModal = ({ profile, currentCarrier, currentPlan, plans, currentPerks, onClose }) => {
  const [showNegotiationTips, setShowNegotiationTips] = useState(false);

  const streamingValue = currentPerks.reduce((total, perk) => total + (perk.value || 0), 0);
  const currentCost = profile.cellularMonthlyCost || currentPlan?.price || 0;
  const effectiveCost = currentCost - streamingValue;

  // Categorize plans
  const categorizePlans = () => {
    const categories = {
      morePerks: [],
      saveMoney: [],
    };

    plans.forEach(plan => {
      if (currentPlan && plan.name === currentPlan.name) return;

      const planPerks = currentCarrier?.perks.filter(p => 
        p.plans.some(planName => planName.toLowerCase().includes(plan.name.toLowerCase()))
      ) || [];
      const planStreamingValue = planPerks.reduce((sum, p) => sum + (p.value || 0), 0);

      // More perks
      if (planPerks.length > currentPerks.length) {
        categories.morePerks.push({
          ...plan,
          perksGained: planPerks.length - currentPerks.length,
          newPerks: planPerks.filter(p => !currentPerks.some(cp => cp.service === p.service)),
          streamingValue: planStreamingValue,
          effectiveCost: plan.price - planStreamingValue,
        });
      }

      // Save money (same or more perks, lower price)
      if (plan.price < (currentPlan?.price || 999) && planPerks.length >= currentPerks.length) {
        categories.saveMoney.push({
          ...plan,
          savings: (currentPlan?.price || 0) - plan.price,
          streamingValue: planStreamingValue,
          effectiveCost: plan.price - planStreamingValue,
        });
      }
    });

    return categories;
  };

  const categories = categorizePlans();
  const hasRecommendations = categories.morePerks.length > 0 || categories.saveMoney.length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Better Plans from {profile.cellularProvider}
            </h3>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Current Plan Summary */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Your Current Plan</h4>
          {currentPlan ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">{currentPlan.name}</p>
                <p className="text-sm text-gray-600">{currentPlan.data}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900">${currentCost.toFixed(2)}/mo</p>
                {streamingValue > 0 && (
                  <>
                    <p className="text-xs text-green-600">- ${streamingValue.toFixed(2)} streaming value</p>
                    <p className="text-sm font-medium text-purple-600">
                      = ${effectiveCost.toFixed(2)}/mo effective
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-600">No plan selected</p>
          )}
        </div>

        <div className="p-6">
          {hasRecommendations ? (
            <div className="space-y-6">
              {/* More Perks */}
              {categories.morePerks.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">🎁 Get More Streaming Perks</h4>
                  <div className="space-y-3">
                    {categories.morePerks.map(plan => (
                      <div key={plan.id} className="border border-green-200 bg-green-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-semibold text-gray-900">{plan.name}</h5>
                            <p className="text-sm text-gray-600">{plan.data}</p>
                            <div className="mt-2">
                              <p className="text-sm text-green-700 font-medium">
                                +{plan.perksGained} more perk{plan.perksGained > 1 ? 's' : ''}:
                              </p>
                              <ul className="mt-1 text-sm text-green-600">
                                {plan.newPerks.map((perk, idx) => (
                                  <li key={idx}>✓ {perk.service} (${perk.value}/mo value)</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">${plan.price}/mo</p>
                            <p className="text-xs text-gray-500">
                              Effective: ${plan.effectiveCost.toFixed(2)}/mo
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Save Money */}
              {categories.saveMoney.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">💰 Save Money</h4>
                  <div className="space-y-3">
                    {categories.saveMoney.map(plan => (
                      <div key={plan.id} className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-semibold text-gray-900">{plan.name}</h5>
                            <p className="text-sm text-gray-600">{plan.data}</p>
                            <p className="mt-2 text-sm text-blue-700 font-medium">
                              Save ${plan.savings}/mo (${plan.savings * 12}/year)
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">${plan.price}/mo</p>
                            {plan.streamingValue > 0 && (
                              <p className="text-xs text-gray-500">
                                Effective: ${plan.effectiveCost.toFixed(2)}/mo
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <h4 className="text-lg font-semibold text-gray-900 mb-2">You're on a great plan!</h4>
              <p className="text-gray-600">
                We don't see any better options for you with {profile.cellularProvider} right now.
              </p>
              {streamingValue > 0 && (
                <p className="mt-2 text-green-600">
                  You're getting ${streamingValue.toFixed(2)}/mo in streaming perks.
                </p>
              )}
            </div>
          )}

          {/* Negotiation Tips */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowNegotiationTips(!showNegotiationTips)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="font-medium text-gray-900">💡 Tips to lower your bill</span>
              <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showNegotiationTips ? 'rotate-90' : ''}`} />
            </button>
            
            {showNegotiationTips && (
              <div className="mt-4 space-y-3">
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="font-medium text-yellow-800 text-sm">📱 Use the carrier's app</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Most carriers show exclusive app-only deals and plan recommendations in their official app.
                  </p>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="font-medium text-blue-800 text-sm">👥 Add more lines</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Family plans often cost less per line. Consider adding family members to save money.
                  </p>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-medium text-green-800 text-sm">💳 Autopay discount</p>
                  <p className="text-xs text-green-700 mt-1">
                    Most carriers offer $5-10/line discount for enabling autopay with a debit card or bank account.
                  </p>
                </div>
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="font-medium text-purple-800 text-sm">🎓 Check for discounts</p>
                  <p className="text-xs text-purple-700 mt-1">
                    Military, first responders, teachers, nurses, and students often qualify for 15-25% off.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Contact Info */}
          {currentCarrier && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-3">Contact {profile.cellularProvider}</p>
              <div className="flex flex-wrap gap-3">
                {currentCarrier.phone && (
                  <a
                    href={`tel:${currentCarrier.phone}`}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="text-sm">{currentCarrier.phone}</span>
                  </a>
                )}
                {currentCarrier.website && (
                  <a
                    href={currentCarrier.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="text-sm">My Account</span>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Compare Cellular Providers Modal
const CompareCellularProvidersModal = ({ profile, carriers, plans, currentCarrier, onClose }) => {
  
  const getCarrierStreamingValue = (carrier) => {
    return carrier.perks.reduce((total, perk) => total + (perk.value || 0), 0);
  };

  const currentStreamingValue = currentCarrier 
    ? getCarrierStreamingValue(currentCarrier)
    : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Compare Cellular Carriers</h3>
              {currentCarrier && (
                <p className="text-sm text-gray-500 mt-1">
                  Current: {currentCarrier.name} • Streaming value: ${currentStreamingValue.toFixed(2)}/mo
                </p>
              )}
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {/* Major carriers first */}
            {carriers.filter(c => ['tmobile', 'verizon', 'att'].includes(c.code)).map(carrier => {
              const carrierPlans = plans[carrier.code] || [];
              const streamingValue = getCarrierStreamingValue(carrier);
              const isCurrentCarrier = carrier.name === profile.cellularProvider;

              return (
                <div 
                  key={carrier.code}
                  className={`border rounded-lg overflow-hidden ${
                    isCurrentCarrier ? 'border-purple-300 bg-purple-50' : 'border-gray-200'
                  }`}
                >
                  <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900">{carrier.name}</h4>
                        {isCurrentCarrier && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Current</span>
                        )}
                      </div>
                      {streamingValue > 0 && (
                        <span className="text-green-600 text-sm">
                          <Gift className="w-4 h-4 inline mr-1" />
                          Up to ${streamingValue.toFixed(2)}/mo in perks
                        </span>
                      )}
                    </div>
                    {carrier.perks.length > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        Perks: {carrier.perks.map(p => p.service).join(', ')}
                      </p>
                    )}
                  </div>

                  <div className="p-4">
                    {carrierPlans.length > 0 ? (
                      <div className="space-y-2">
                        {carrierPlans.map(plan => (
                          <div key={plan.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <div>
                              <span className="font-medium text-gray-900">{plan.name}</span>
                              <span className="text-sm text-gray-500 ml-2">{plan.data}</span>
                              {plan.perks.length > 0 && (
                                <p className="text-xs text-green-600 mt-0.5">
                                  Includes: {plan.perks.join(', ')}
                                </p>
                              )}
                            </div>
                            <span className="font-medium text-gray-900">${plan.price}/line</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Contact carrier for pricing.</p>
                    )}

                    {!isCurrentCarrier && carrier.website && (
                      <a 
                        href={carrier.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors inline-block text-center"
                      >
                        View Plans & Switch →
                      </a>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Budget carriers */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Budget-Friendly Options</h4>
              <div className="grid grid-cols-2 gap-3">
                {carriers.filter(c => !['tmobile', 'verizon', 'att'].includes(c.code)).map(carrier => (
                  <div key={carrier.code} className="border border-gray-200 rounded-lg p-3">
                    <h5 className="font-medium text-gray-900">{carrier.name}</h5>
                    {carrier.perks.length > 0 ? (
                      <p className="text-xs text-green-600 mt-1">
                        Includes: {carrier.perks.map(p => p.service).join(', ')}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">No streaming perks</p>
                    )}
                    {carrier.website && (
                      <a
                        href={carrier.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 text-xs text-purple-600 hover:text-purple-800 inline-flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Learn more
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-400 text-center mt-6">
            Prices and perks may vary. Verify with carrier before switching.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CellularSection;